import {useRef, useEffect, useState, useCallback} from "react";
import {models} from "powerbi-client";
import {ZOOM_MIN, ZOOM_MAX} from "./constants";
import PrintGuideModal from "./PrintGuideModal";

// ─── Icons (inline SVG, 16×16 viewBox) ────────────────────────────────────────

const IconMinus = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round">
        <line x1="3" y1="8" x2="13" y2="8"/>
    </svg>
);

const IconPlus = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round">
        <line x1="8" y1="3" x2="8" y2="13"/>
        <line x1="3" y1="8" x2="13" y2="8"/>
    </svg>
);

const IconFullscreen = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round">
        <polyline points="10,2 14,2 14,6"/>
        <polyline points="6,14 2,14 2,10"/>
        <line x1="14" y1="2" x2="9" y2="7"/>
        <line x1="2" y1="14" x2="7" y2="9"/>
    </svg>
);

const IconDots = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
        <circle cx="8" cy="3" r="1.4"/>
        <circle cx="8" cy="8" r="1.4"/>
        <circle cx="8" cy="13" r="1.4"/>
    </svg>
);

const IconCheck = () => (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round">
        <polyline points="2,9 6,13 14,4"/>
    </svg>
);

const IconRefresh = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round">
        <path d="M13.5 2.5v4h-4"/>
        <path d="M13.5 6.5A6 6 0 1 1 10 3.1"/>
    </svg>
);

const IconPrint = () => (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="1" width="10" height="5" rx="1"/>
        <path d="M3 11H1.5A.5.5 0 0 1 1 10.5v-5A.5.5 0 0 1 1.5 5h13a.5.5 0 0 1 .5.5v5a.5.5 0 0 1-.5.5H13"/>
        <rect x="3" y="9" width="10" height="6" rx="1"/>
        <line x1="5" y1="12" x2="11" y2="12"/>
        <line x1="5" y1="14" x2="9" y2="14"/>
    </svg>
);

// ─── Styles ────────────────────────────────────────────────────────────────────

const S = {
    bar: {
        display: "flex",
        alignItems: "center",
        height: "40px",
        padding: "0 10px",
        gap: "4px",
        borderTop: "1px solid #e2e8f0",
        backgroundColor: "#f8fafc",
        flexShrink: 0,
        boxSizing: "border-box",
        userSelect: "none",
    },
    pageScroller: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
        flex: "1 1 0",
        minWidth: 0,
        overflowX: "auto",
        overflowY: "hidden",
        scrollbarWidth: "none",        // Firefox
        msOverflowStyle: "none",       // IE/Edge
    },
    pageBtn: (active) => ({
        padding: "3px 12px",
        fontSize: "12px",
        fontWeight: active ? 600 : 400,
        color: active ? "#ffffff" : "#374151",
        backgroundColor: active ? "#1e3a5f" : "transparent",
        border: `1px solid ${active ? "#1e3a5f" : "#d1d5db"}`,
        borderRadius: "4px",
        cursor: "pointer",
        whiteSpace: "nowrap",
        flexShrink: 0,
        transition: "all 0.12s ease",
        lineHeight: "1.6",
    }),
    divider: {
        width: "1px",
        height: "20px",
        background: "#e2e8f0",
        flexShrink: 0,
        margin: "0 2px",
    },
    iconBtn: (disabled = false) => ({
        width: "26px",
        height: "26px",
        borderRadius: "4px",
        border: "1px solid #e2e8f0",
        backgroundColor: "transparent",
        color: disabled ? "#9ca3af" : "#374151",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        padding: 0,
        transition: "background 0.1s ease",
    }),
    zoomLabel: {
        fontSize: "12px",
        fontWeight: 600,
        color: "#374151",
        minWidth: "38px",
        textAlign: "center",
        fontVariantNumeric: "tabular-nums",
        flexShrink: 0,
        cursor: "default",
    },
    dropdown: {
        position: "absolute",
        bottom: "34px",
        right: 0,
        backgroundColor: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "6px",
        padding: "4px",
        minWidth: "172px",
        zIndex: 200,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    },
    dropdownSection: {
        fontSize: "10px",
        color: "#9ca3af",
        padding: "4px 8px 2px",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        margin: 0,
    },
    dropdownBtn: (active = false) => ({
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "100%",
        padding: "6px 8px",
        fontSize: "13px",
        border: "none",
        borderRadius: "4px",
        backgroundColor: active ? "#1e3a5f" : "transparent",
        color: active ? "#ffffff" : "#374151",
        cursor: "pointer",
        textAlign: "left",
        boxSizing: "border-box",
        transition: "background 0.1s ease",
    }),
    dropdownDivider: {
        height: "1px",
        background: "#e2e8f0",
        margin: "4px 0",
    },
};

// ─── Display mode options ──────────────────────────────────────────────────────

const DISPLAY_OPTIONS = [
    {value: "fitToWidth", label: "Fit to width", icon: "↔", modelVal: models.DisplayOption.FitToWidth},
    {value: "fitToPage", label: "Fit to page", icon: "⤢", modelVal: models.DisplayOption.FitToPage},
    {value: "actualSize", label: "Actual size", icon: "⊡", modelVal: models.DisplayOption.ActualSize},
];

// ─── BottomBar ─────────────────────────────────────────────────────────────────

const BottomBar = ({
                       // page nav
                       pages,
                       activePage,
                       isNavigating,
                       onPageChange,
                       // toolbar
                       isReady,
                       zoom,
                       displayMode,
                       isRefreshing,
                       onZoomOut,
                       onZoomIn,
                       onRefresh,
                       onPrint,
                       onFullscreen,
                       onDisplayMode,
                       // layout
                       zenView,
                   }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [printModal, setPrintModal] = useState(false);
    const menuRef = useRef(null);
    const scrollerRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        if (!menuOpen) return;
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [menuOpen]);

    // Scroll active page tab into view
    useEffect(() => {
        if (!scrollerRef.current || !activePage) return;
        const activeBtn = scrollerRef.current.querySelector("[data-active='true']");
        if (activeBtn) activeBtn.scrollIntoView({inline: "nearest", behavior: "smooth"});
    }, [activePage]);

    const handleDisplayMode = useCallback((value) => {
        setMenuOpen(false);
        onDisplayMode(value);
    }, [onDisplayMode]);

    // Don't render the bar if there's nothing to show
    const showPages = pages.length > 1;
    const showToolbar = zenView;
    if (!showPages && !showToolbar) return null;

    return (
        <div style={S.bar}>

            {/* ── Page tabs (scrollable, left side) ── */}
            <div
                ref={scrollerRef}
                style={{
                    ...S.pageScroller,
                    // fade-out right edge only when pages overflow
                    WebkitMaskImage: showPages
                        ? "linear-gradient(to right, black 85%, transparent 100%)"
                        : "none",
                    maskImage: showPages
                        ? "linear-gradient(to right, black 85%, transparent 100%)"
                        : "none",
                }}
            >
                {showPages && pages.map((page) => {
                    const active = page.name === activePage;
                    return (
                        <button
                            key={page.name}
                            data-active={active}
                            onClick={() => !isNavigating && onPageChange(page.name)}
                            disabled={isNavigating}
                            style={S.pageBtn(active)}
                        >
                            {page.displayName}
                        </button>
                    );
                })}
            </div>

            {/* ── Toolbar controls (right side, only in zenView) ── */}
            {showToolbar && (
                <>
                    {showPages && <div style={S.divider}/>}

                    {/* Zoom out */}
                    <button
                        style={S.iconBtn(!isReady || zoom <= ZOOM_MIN)}
                        disabled={!isReady || zoom <= ZOOM_MIN}
                        onClick={onZoomOut}
                        title="Zoom out"
                    >
                        <IconMinus/>
                    </button>

                    {/* Zoom % */}
                    <span style={S.zoomLabel} title="Current zoom">
                        {Math.round(zoom * 100)}%
                    </span>

                    {/* Zoom in */}
                    <button
                        style={S.iconBtn(!isReady || zoom >= ZOOM_MAX)}
                        disabled={!isReady || zoom >= ZOOM_MAX}
                        onClick={onZoomIn}
                        title="Zoom in"
                    >
                        <IconPlus/>
                    </button>

                    <div style={S.divider}/>

                    {/* Fullscreen */}
                    <button
                        style={S.iconBtn(!isReady)}
                        disabled={!isReady}
                        onClick={onFullscreen}
                        title="Fullscreen"
                    >
                        <IconFullscreen/>
                    </button>

                    {/* ⋮ More menu */}
                    <div ref={menuRef} style={{position: "relative", flexShrink: 0}}>
                        <button
                            style={S.iconBtn(!isReady)}
                            disabled={!isReady}
                            onClick={() => setMenuOpen((o) => !o)}
                            title="More options"
                        >
                            <IconDots/>
                        </button>

                        {menuOpen && (
                            <div style={S.dropdown}>
                                <p style={S.dropdownSection}>Display</p>

                                {DISPLAY_OPTIONS.map(({value, label, icon}) => {
                                    const active = displayMode === value;
                                    return (
                                        <button
                                            key={value}
                                            style={S.dropdownBtn(active)}
                                            onClick={() => handleDisplayMode(value)}
                                        >
                                            <span style={{fontSize: "15px", lineHeight: 1}}>{icon}</span>
                                            {label}
                                            {active && (
                                                <span style={{marginLeft: "auto"}}>
                                                    <IconCheck/>
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}

                                <div style={S.dropdownDivider}/>

                                <button
                                    style={S.dropdownBtn()}
                                    disabled={isRefreshing}
                                    onClick={() => {
                                        setMenuOpen(false);
                                        onRefresh();
                                    }}
                                >
                                    <IconRefresh/>
                                    {isRefreshing ? "Refreshing…" : "Refresh"}
                                </button>

                                <button
                                    style={S.dropdownBtn()}
                                    onClick={() => {
                                        setMenuOpen(false);
                                        setPrintModal(true);
                                    }}
                                >
                                    <IconPrint/>
                                    Print
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}

            {printModal && (
                <PrintGuideModal
                    onCancel={() => setPrintModal(false)}
                    onPrint={() => {
                        setPrintModal(false);
                        onPrint();
                    }}
                />
            )}
        </div>
    );
};

export default BottomBar;